import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, Maximize2, RefreshCw, AlertTriangle, ArrowUpRight, Plus, X, Check, Package } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const INWARD_APP_URL = 'https://tirth-gate-ims-1060882894254.us-west1.run.app/';

const EMPTY_ITEM = { material_id: '', material_name: '', qty: '', unit: 'kg', rate: '', mfg_date: '', expiry_date: '', quality: 'Good' };

export default function InwardLot() {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const iframeRef = useRef(null);

  const [showGrn, setShowGrn] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [grnForm, setGrnForm] = useState({ vendor_id: '', vendor_name: '', vendor_bill_no: '', received_date: new Date().toISOString().slice(0, 10), notes: '' });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [submitting, setSubmitting] = useState(false);
  const [recentGrns, setRecentGrns] = useState([]);

  useEffect(() => {
    setLoading(true); setFailed(false);
    const t = setTimeout(() => { if (loading) setFailed(true); }, 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const loadGrnData = async () => {
    try {
      const [vRes, mRes, gRes] = await Promise.all([
        axios.get(`${API}/clients/?type=Creditor`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/inventory/raw-materials`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/inward/list`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setVendors((vRes.data || []).filter(c => c.type === 'Creditor' || c.type === 'Both'));
      setMaterials(mRes.data || []);
      setRecentGrns((gRes.data || []).slice(0, 5));
    } catch { /* ignore */ }
  };
  useEffect(() => { loadGrnData(); }, []);

  const reload = () => { setReloadKey(k => k + 1); };
  const openFullscreen = () => {
    const f = iframeRef.current;
    if (!f) return;
    if (f.requestFullscreen) f.requestFullscreen();
    else if (f.webkitRequestFullscreen) f.webkitRequestFullscreen();
  };

  const setItem = (i, k, v) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addItem = () => setItems(prev => [...prev, { ...EMPTY_ITEM }]);
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const submitGrn = async () => {
    if (!grnForm.vendor_name) { toast.error('Select vendor'); return; }
    if (!items.length || !items.some(it => it.material_id && it.qty)) { toast.error('Add at least one material'); return; }
    setSubmitting(true);
    try {
      const payload = {
        vendor_id: grnForm.vendor_id,
        vendor_name: grnForm.vendor_name,
        vendor_bill_no: grnForm.vendor_bill_no,
        received_date: grnForm.received_date,
        notes: grnForm.notes,
        items: items.filter(it => it.material_id && it.qty).map(it => ({
          material_id: it.material_id,
          material_name: it.material_name,
          qty: parseFloat(it.qty),
          unit: it.unit || 'kg',
          rate: parseFloat(it.rate || 0),
          mfg_date: it.mfg_date || '',
          expiry_date: it.expiry_date || '',
          quality: it.quality || 'Good',
        })),
      };
      const res = await axios.post(`${API}/inward/create`, payload, { withCredentials: true });
      toast.success(`GRN ${res.data.id} created · ${res.data.lots_created?.length || 0} lot(s)`);
      setShowGrn(false);
      setItems([{ ...EMPTY_ITEM }]);
      setGrnForm({ vendor_id: '', vendor_name: '', vendor_bill_no: '', received_date: new Date().toISOString().slice(0, 10), notes: '' });
      loadGrnData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create GRN'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-72px)] -mx-4 sm:-mx-6 -my-4" data-testid="inward-lot-page">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-zinc-900/70 backdrop-blur">
        <div>
          <h1 className="font-heading font-semibold text-zinc-50 text-lg flex items-center gap-2">
            Inward + Lot
            <span className="text-[10px] font-normal bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full border border-amber-500/30">
              External App
            </span>
          </h1>
          <div className="text-zinc-500 text-xs mt-0.5">Tirth Gate IMS · material inward & lot tracking</div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setShowGrn(true)} data-testid="new-grn-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-xs font-semibold transition-colors">
            <Plus size={13} /> New GRN
          </button>
          <button data-testid="inward-reload-btn" onClick={reload}
            title="Reload"
            className="p-2 text-zinc-400 hover:text-zinc-100 rounded-lg hover:bg-zinc-800 transition-colors">
            <RefreshCw size={14} />
          </button>
          <button data-testid="inward-fullscreen-btn" onClick={openFullscreen}
            title="Fullscreen"
            className="p-2 text-zinc-400 hover:text-zinc-100 rounded-lg hover:bg-zinc-800 transition-colors">
            <Maximize2 size={14} />
          </button>
          <a data-testid="inward-open-new-tab"
            href={INWARD_APP_URL} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/30 rounded-lg text-xs font-semibold transition-colors">
            <ExternalLink size={13} /> Open in new tab
          </a>
        </div>
      </div>

      {/* Recent GRNs strip */}
      {recentGrns.length > 0 && (
        <div className="px-5 py-2.5 border-b border-zinc-800 bg-zinc-950" data-testid="recent-grns">
          <div className="flex items-center gap-2 overflow-x-auto">
            <Package size={12} className="text-zinc-500 flex-shrink-0" />
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider flex-shrink-0 mr-2">Recent GRNs:</span>
            {recentGrns.map(g => (
              <div key={g.id} className="flex-shrink-0 text-[10px] bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1">
                <span className="font-mono text-amber-300">{g.id}</span>
                <span className="text-zinc-500 ml-1.5">· {g.vendor_name}</span>
                <span className="text-zinc-600 ml-1.5">· {(g.lots_created || []).length} lot(s)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Embedded app / fallback */}
      <div className="relative flex-1 bg-zinc-950">
        {loading && !failed && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-zinc-950/80 backdrop-blur-sm">
            <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            <div className="text-zinc-400 text-sm mt-4">Loading Inward + Lot app…</div>
          </div>
        )}
        {failed ? (
          <div className="absolute inset-0 flex items-center justify-center p-8" data-testid="inward-fallback">
            <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={20} />
              </div>
              <div className="text-zinc-50 text-sm font-semibold">This app blocks embedding</div>
              <div className="text-zinc-500 text-xs mt-2 leading-relaxed">
                The external Inward + Lot app prevents itself from being loaded inside another page (X-Frame-Options / CSP).
                Open it in a new tab instead — you'll stay signed in there.
              </div>
              <a href={INWARD_APP_URL} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold rounded-xl">
                Open Inward + Lot <ArrowUpRight size={14} />
              </a>
              <div className="mt-3">
                <button onClick={reload} className="text-[11px] text-zinc-500 hover:text-zinc-300">
                  Try loading here again
                </button>
              </div>
            </div>
          </div>
        ) : (
          <iframe
            key={reloadKey}
            ref={iframeRef}
            data-testid="inward-iframe"
            src={INWARD_APP_URL}
            title="Tirth Gate IMS — Inward + Lot"
            className="absolute inset-0 w-full h-full border-0 bg-white"
            onLoad={() => { setLoading(false); setFailed(false); }}
            allow="clipboard-read; clipboard-write; camera; microphone; fullscreen"
          />
        )}
      </div>

      {/* ═══ GRN Quick-Entry Modal ═══ */}
      {showGrn && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowGrn(false)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="grn-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
              <div>
                <h3 className="font-heading font-semibold text-zinc-50 text-base">New GRN — Goods Receipt Note</h3>
                <p className="text-zinc-500 text-xs mt-0.5">Records material inward, creates FIFO lots with MFG + Expiry dates.</p>
              </div>
              <button onClick={() => setShowGrn(false)} className="text-zinc-500 hover:text-zinc-200"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Vendor *</label>
                  <select value={grnForm.vendor_id}
                    onChange={e => { const v = vendors.find(x => x.id === e.target.value); setGrnForm(f => ({ ...f, vendor_id: e.target.value, vendor_name: v?.name || '' })); }}
                    data-testid="grn-vendor"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm">
                    <option value="">Select vendor…</option>
                    {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Vendor Bill No.</label>
                  <input value={grnForm.vendor_bill_no} onChange={e => setGrnForm(f => ({ ...f, vendor_bill_no: e.target.value }))}
                    data-testid="grn-bill-no" placeholder="Invoice / challan ref"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Received Date</label>
                  <input type="date" value={grnForm.received_date} onChange={e => setGrnForm(f => ({ ...f, received_date: e.target.value }))}
                    data-testid="grn-received-date"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-zinc-300 text-xs font-semibold">Materials Received</label>
                  <button onClick={addItem} className="text-xs text-amber-300 flex items-center gap-1 hover:text-amber-200"><Plus size={12} /> Add Row</button>
                </div>
                <div className="overflow-x-auto bg-zinc-900 border border-zinc-800 rounded-lg">
                  <table className="w-full text-xs min-w-[900px]">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-zinc-950/50">
                        {['Material', 'Qty', 'Unit', 'Rate', 'MFG Date', 'Expiry', 'Quality', ''].map(h => (
                          <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-2 py-2">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, i) => (
                        <tr key={i} className="border-b border-zinc-800/50">
                          <td className="px-2 py-1.5">
                            <select value={it.material_id}
                              onChange={e => { const m = materials.find(x => x.id === e.target.value); setItem(i, 'material_id', e.target.value); setItem(i, 'material_name', m?.name || ''); setItem(i, 'unit', m?.unit || 'kg'); }}
                              data-testid={`grn-material-${i}`}
                              className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 text-xs">
                              <option value="">Select…</option>
                              {materials.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1.5"><input type="number" value={it.qty} onChange={e => setItem(i, 'qty', e.target.value)} data-testid={`grn-qty-${i}`} className="w-20 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 text-xs text-right" /></td>
                          <td className="px-2 py-1.5 text-zinc-400 text-xs">{it.unit || 'kg'}</td>
                          <td className="px-2 py-1.5"><input type="number" value={it.rate} onChange={e => setItem(i, 'rate', e.target.value)} data-testid={`grn-rate-${i}`} className="w-24 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 text-xs text-right" /></td>
                          <td className="px-2 py-1.5"><input type="date" value={it.mfg_date} onChange={e => setItem(i, 'mfg_date', e.target.value)} data-testid={`grn-mfg-${i}`} className="w-32 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 text-xs" /></td>
                          <td className="px-2 py-1.5"><input type="date" value={it.expiry_date} onChange={e => setItem(i, 'expiry_date', e.target.value)} data-testid={`grn-expiry-${i}`} className="w-32 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 text-xs" /></td>
                          <td className="px-2 py-1.5">
                            <select value={it.quality} onChange={e => setItem(i, 'quality', e.target.value)}
                              className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 text-xs">
                              {['Good', 'Average', 'Rejected'].map(q => <option key={q}>{q}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            {items.length > 1 && <button onClick={() => removeItem(i)} className="text-zinc-500 hover:text-red-400"><X size={12} /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Notes</label>
                <textarea rows={2} value={grnForm.notes} onChange={e => setGrnForm(f => ({ ...f, notes: e.target.value }))}
                  data-testid="grn-notes" placeholder="Optional remarks"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm resize-none" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowGrn(false)} className="px-4 py-2 bg-zinc-800 text-zinc-300 rounded-lg text-xs">Cancel</button>
                <button onClick={submitGrn} disabled={submitting} data-testid="grn-submit"
                  className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black rounded-lg text-xs font-semibold">
                  <Check size={13} /> {submitting ? 'Creating…' : 'Create GRN'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
